import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMinus,
  faPlus,
  faXmark,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import { CCMCrossICon, CCMFileIcon, userIcon } from "../../tabsvgs";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import ToastMessage from "../../../../../utils/mui/righttoptoast";

const REASONS = [
  "Chart Review",
  "Care Coordination",
  "Medication Review",
  "Care Plan Development",
  "Patient Phone Call",
  "Other",
];

const clampInt = (v, min, max) => Math.max(min, Math.min(max, v));
const toInt = (v) => (Number.isFinite(+v) ? Math.trunc(+v) : 0);

const Dot = () => <span className="mx-2 text-[#9CA3AF]">•</span>;

const ItemCard = ({ item }) => {
  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white p-3 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="scale-[1.05]">{userIcon}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-inter text-left font-semibold text-[#1F2937]">
            {item.provider}
          </div>

          <div className="mt-[2px] flex flex-wrap items-center font-inter font-medium text-[12px] leading-5 text-[#6B6B6B]">
            <span>{item.minutes}m</span>
            <Dot />
            <span>{item.category}</span>
          </div>

          {item.note ? (
            <div className="mt-1 text-[12px] font-inter text-[#4B5563] text-left whitespace-pre-wrap break-words">
              {item.note}
            </div>
          ) : null}

          <div className="mt-1 text-[10px] font-inter text-left leading-5 text-[#9CA3AF]">
            {item.timestamp}
          </div>
        </div>
      </div>
    </div>
  );
};

const AddTimeEntryModal = ({
  open,
  onClose,
  auditData,
  selectedRecord,
  insertAuditData,
  initialMinutes = selectedRecord?.duration || 0,
}) => {
  const [minutes, setMinutes] = useState(clampInt(initialMinutes, 1, 240));
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [alertmessage, setAlertMessage] = useState("");
  const [severity, setSeverity] = useState("info");
  const [openToast, setOpenToast] = useState(false);

  const userConfig = useSelector((state) => state.userconfig);
  const isOther = reason === "Other";

  // Reset form when modal opens
  useEffect(() => {
    if (!open) return;
    setMinutes(clampInt(initialMinutes, 1, 240));
    setReason("");
    setNote("");
  }, [open, initialMinutes]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (!open) return;
    document.body.classList.add("overflow-hidden");
    return () => document.body.classList.remove("overflow-hidden");
  }, [open]);

  const handleCloseToast = () => setOpenToast(false);

  const handleMinus = () => setMinutes((m) => clampInt(m - 5, 1, 240));
  const handlePlus = () => setMinutes((m) => clampInt(m + 5, 1, 240));
  const handleManual = (e) => {
    const n = toInt(e.target.value);
    setMinutes(clampInt(n, 1, 240));
  };

  const handleSave = async () => {
    // ✅ Validate required fields
    if (!reason) {
      setAlertMessage("Please select a reason before saving.");
      setSeverity("error");
      setOpenToast(true);
      return;
    }

    if (isOther && (!note || !note.trim())) {
      setAlertMessage("Please enter a note for the 'Other' reason.");
      setSeverity("error");
      setOpenToast(true);
      return;
    }

    const olddata = auditData?.record || [];
    const newEntry = {
      minutes,
      reason,
      note: isOther ? note.trim() : "",
      user: userConfig?.username,
      timestamp: dayjs().format("YYYY-MM-DD HH:mm:ss"),
    };

    const combinedData = [
      ...olddata.filter((entry) => Object.keys(entry || {}).length > 0),
      newEntry,
    ];

    try {
      await insertAuditData({
        audiolink: selectedRecord.audiolink,
        data: { record: combinedData },
        category: "audit_trail",
      });

      setAlertMessage("Time entry added successfully!");
      setSeverity("success");
      setOpenToast(true);
      onClose(); // close modal after saving
    } catch (error) {
      setAlertMessage("Failed to save time entry. Please try again.");
      setSeverity("error");
      setOpenToast(true);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-7 pb-3 pt-5 flex-shrink-0">
          <h2 className="text-[13px] font-inter font-semibold text-[#1F2937]">
            Add Time Entry
          </h2>
          <button
            onClick={onClose}
            className="focus:outline-none"
            aria-label="Close"
          >
            <FontAwesomeIcon
              className="text-[#6B7280] text-[18px]"
              icon={faXmark}
            />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-7 pb-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            {/* Minutes input */}
            <div>
              <label className="block text-xs font-inter font-medium text-[#4B5563] mb-2 text-left">
                Time (minutes)
              </label>
              <div className="flex items-stretch gap-3">
                <button
                  type="button"
                  onClick={handleMinus}
                  className="h-10 w-8 rounded-[10px] border border-[#E5E7EB] bg-white flex items-center justify-center focus:outline-none"
                >
                  <FontAwesomeIcon icon={faMinus} />
                </button>

                <input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={String(minutes)}
                  onChange={handleManual}
                  className="h-10 w-full max-w-[220px] text-center text-[20px] font-semibold rounded-[10px] border border-[#E5E7EB] focus:outline-none"
                />

                <button
                  type="button"
                  onClick={handlePlus}
                  className="h-10 w-8 rounded-[10px] border border-[#E5E7EB] bg-[#0F1C3F] text-white flex items-center justify-center focus:outline-none"
                >
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              </div>
              <p className="mt-2 text-[10px] font-inter font-medium text-[#4B5563] text-left">
                Click + to add 5 minutes or enter manually
              </p>
            </div>

            {/* Reason dropdown */}
            <div>
              <label className="block text-[12px] text-[#4B5563] font-inter font-medium mb-2 text-left">
                Reason
              </label>
              <div className="relative">
                <select
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="h-10 w-full appearance-none rounded-[10px] border border-[#E5E7EB] pl-4 pr-10 text-[#374151] outline-none"
                >
                  <option value="" disabled>
                    Select a reason...
                  </option>
                  {REASONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280]">
                  <FontAwesomeIcon icon={faChevronDown} />
                </div>
              </div>
            </div>
          </div>

          {/* Note input (only for "Other") */}
          {isOther && (
            <div className="my-3">
              <label className="block text-sm font-medium text-[#6B7280] mb-2 text-left">
                Note (required for “Other”)
              </label>
              <textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a short note…"
                className="w-full rounded-[10px] border border-[#E5E7EB] p-3 outline-none"
              />
            </div>
          )}

          {/* Audit Trail */}
          <h3 className="text-[18px] text-left font-semibold text-[#111827] mb-4">
            Audit Trail
          </h3>
          <div className="space-y-3">
            {auditData?.record && auditData.record.length > 0 ? (
              auditData.record
                .filter((entry) => Object.keys(entry || {}).length > 0)
                .map((it, index) => (
                  <ItemCard
                    key={index}
                    item={{
                      provider: it.user || "Unknown User",
                      minutes: it.minutes || 0,
                      category: it.reason || "N/A",
                      note: it.note || "",
                      timestamp: it.timestamp
                        ? dayjs(it.timestamp).format("DD/MM/YYYY, hh:mm A")
                        : "",
                    }}
                  />
                ))
            ) : (
              <div className="text-sm text-[#6B7280]">No entries yet.</div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-3 px-7 py-3 rounded-b-2xl">
          <button
            type="button"
            onClick={handleSave}
            className={`px-3 py-2 rounded-lg focus:outline-none text-[12px] bg-[#E5E7EB] flex items-center gap-2 text-[#374151] font-medium transition hover:!bg-[#1D2951] hover:text-white`}
          >
            {CCMFileIcon} Save
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-lg text-[12px] focus:outline-none bg-[#E5E7EB] flex items-center gap-1 text-[#374151] font-medium hover:!bg-[#1D2951] hover:text-white transition"
          >
            {CCMCrossICon} Cancel
          </button>
        </div>
      </div>

      {/* ✅ Toast Message */}
      <ToastMessage
        message={alertmessage}
        severity={severity}
        open={openToast}
        handleClose={handleCloseToast}
      />
    </div>
  );
};

export default AddTimeEntryModal;
